import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header } from '@ah/design/mobile';
import {
  Mountain, BookOpen, Palette, Plane, Activity, Heart,
  type LucideIcon,
} from 'lucide-react';
import {
  BUCKET_LIST_ITEM_Q,
  MY_BUCKET_LIST_Q,
  RESPOND_BUCKET_LIST_ITEM_M,
  REMOVE_BUCKET_LIST_ITEM_M,
  type BucketListItem,
  type BucketListCategory,
} from '../shared/index.ts';

const CATEGORY_ICON: Record<BucketListCategory, LucideIcon> = {
  adventure: Mountain,
  learning:  BookOpen,
  creative:  Palette,
  travel:    Plane,
  physical:  Activity,
  impact:    Heart,
};

const CATEGORY_LABEL: Record<BucketListCategory, string> = {
  adventure: 'Adventure',
  learning:  'Learning',
  creative:  'Creative',
  travel:    'Travel',
  physical:  'Physical',
  impact:    'Impact',
};

export function BucketListItemDetail() {
  const { id } = useParams<{ id: string }>();
  const gql = useGql();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const itemQuery = useQuery({
    queryKey: ['bucketlist', 'item', id],
    queryFn: async () => {
      const res = await gql.request<{ bucketListItem: BucketListItem | null }>(
        BUCKET_LIST_ITEM_Q,
        { id },
      );
      return res.bucketListItem;
    },
    enabled: !!id,
  });

  const myList = useQuery({
    queryKey: ['bucketlist', 'mine'],
    queryFn: async () => {
      const res = await gql.request<{ myBucketList: BucketListItem[] }>(MY_BUCKET_LIST_Q);
      return res.myBucketList;
    },
  });

  const isOnList = !!myList.data?.some((i) => i.id === id);

  const add = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await gql.request(RESPOND_BUCKET_LIST_ITEM_M, { itemId: id, status: 'added' });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['bucketlist'] });
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await gql.request(REMOVE_BUCKET_LIST_ITEM_M, { itemId: id });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['bucketlist'] });
    },
  });

  const item = itemQuery.data ?? null;
  const backTo = isOnList ? '/my-life/bucket-list' : '/my-life/bucket-list/suggested';

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Bucket List" backTo={backTo} />
      <main className="flex-1 overflow-y-auto">
        {!item ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm">
            {itemQuery.isLoading ? 'Loading…' : 'Item not found.'}
          </div>
        ) : (
          <>
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                className="w-full aspect-video object-cover bg-muted"
              />
            ) : (
              (() => {
                const Icon = CATEGORY_ICON[item.category];
                return (
                  <div className="w-full aspect-video bg-primary/10 flex items-center justify-center">
                    <Icon size={56} className="text-primary/70" />
                  </div>
                );
              })()
            )}
            <div className="px-4 py-5 space-y-3">
              <span className="inline-block bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
                {CATEGORY_LABEL[item.category]}
              </span>
              <h1 className="text-2xl font-bold leading-tight">{item.label}</h1>
              <p className="text-[15px] text-foreground/85 leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            </div>
          </>
        )}
      </main>
      {item && (
        <footer
          className="shrink-0 bg-background/95 backdrop-blur-sm border-t border-border px-4"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="py-3">
            {isOnList ? (
              <button
                type="button"
                onClick={async () => {
                  await remove.mutateAsync();
                  navigate('/my-life/bucket-list');
                }}
                disabled={remove.isPending}
                className="w-full flex items-center justify-center h-12 rounded-full border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-40 active:scale-[0.98] transition-all"
              >
                {remove.isPending ? 'Removing…' : 'Remove from my list'}
              </button>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  await add.mutateAsync();
                  navigate('/my-life/bucket-list');
                }}
                disabled={add.isPending}
                className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-40 active:scale-[0.98] transition-all shadow-md shadow-primary/20"
              >
                {add.isPending ? 'Adding…' : 'Add to my list'}
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
